// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// UI language: explicit user choice (localStorage) wins over browser locale.
const LANG_KEY = 'cme_ui_lang';
const WELCOME_LANGS = ['it', 'en', 'fr', 'es'];
export const getWelcomeLang = () => {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (WELCOME_LANGS.includes(saved)) return saved;
  } catch {
    /* private mode */
  }
  const nav = (typeof navigator !== 'undefined' && (navigator.language || '').toLowerCase()) || '';
  const prefix = nav.split('-')[0];
  return WELCOME_LANGS.includes(prefix) ? prefix : 'en';
};
export const setWelcomeLang = (lang) => {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* private mode */
  }
};

const STRINGS = {
  it: {
    needHelp: 'Bisogno di aiuto?',
    subtitle:
      'Ecco cosa possiamo fare insieme. Clicca una card per scoprirlo — ti rispondo direttamente in chat, poi chiedimi pure qualsiasi cosa.',
    hideLabel: 'Nascondi guida',
    guideButton: 'Guida',
    guideTooltip: 'Bisogno di aiuto? Mostra la guida',
    capabilities: [
      {
        icon: '🧭',
        title: 'Panoramica',
        description: 'Cosa sa fare questo assistente',
        prompt: 'Cosa puoi fare? Dammi una panoramica.',
      },
      {
        icon: '📧',
        title: 'Email',
        description: 'Cerca, riepiloga, prepara bozze di risposta',
        prompt: 'Come puoi aiutarmi con le email?',
      },
      {
        icon: '📅',
        title: 'Calendario',
        description: 'Consulta e crea eventi',
        prompt: 'Come funziona il calendario?',
      },
      {
        icon: '📁',
        title: 'File e export',
        description: 'Legge file ed esporta in Excel, PDF, Word',
        prompt: 'Quali file puoi leggere e come funzionano gli export?',
      },
      {
        icon: '🤖',
        title: 'Specialisti',
        description: 'Vendite, contabilità, magazzino e altri',
        prompt: 'Quali specialisti puoi usare e quando li usi?',
      },
      {
        icon: '⏰',
        title: 'Attività ricorrenti',
        description: 'Job automatici con esito via email',
        prompt: 'Come funzionano le attività ricorrenti?',
      },
      {
        icon: '🧠',
        title: 'Memoria',
        description: 'Ricorda le preferenze che gli salvi',
        prompt: 'Cosa puoi ricordare di me e come funziona la memoria?',
      },
      {
        icon: '🌐',
        title: 'Ricerca web',
        description: 'Notizie e dati aggiornati',
        prompt: 'Come funziona la ricerca web?',
      },
    ],
  },
  en: {
    needHelp: 'Need help?',
    subtitle:
      "Here's what we can do together. Click a card to find out — I'll answer right here in chat, then ask me anything.",
    hideLabel: 'Hide guide',
    guideButton: 'Guide',
    guideTooltip: 'Need help? Show the guide',
    capabilities: [
      {
        icon: '🧭',
        title: 'Overview',
        description: 'What this assistant can do',
        prompt: 'What can you do? Give me an overview.',
      },
      {
        icon: '📧',
        title: 'Email',
        description: 'Search, summarize, draft replies',
        prompt: 'How can you help me with my email?',
      },
      {
        icon: '📅',
        title: 'Calendar',
        description: 'Check and create events',
        prompt: 'How does the calendar work?',
      },
      {
        icon: '📁',
        title: 'Files & exports',
        description: 'Reads files, exports to Excel, PDF, Word',
        prompt: 'Which files can you read and how do exports work?',
      },
      {
        icon: '🤖',
        title: 'Specialists',
        description: 'Sales, accounting, warehouse and more',
        prompt: 'Which specialists can you use and when?',
      },
      {
        icon: '⏰',
        title: 'Recurring tasks',
        description: 'Automatic jobs with email delivery',
        prompt: 'How do recurring tasks work?',
      },
      {
        icon: '🧠',
        title: 'Memory',
        description: 'Remembers the preferences you save',
        prompt: 'What can you remember about me and how does memory work?',
      },
      {
        icon: '🌐',
        title: 'Web search',
        description: 'Up-to-date news and data',
        prompt: 'How does web search work?',
      },
    ],
  },
  fr: {
    needHelp: "Besoin d'aide ?",
    subtitle:
      "Voici ce que nous pouvons faire ensemble. Clique sur une carte pour le découvrir — je te réponds directement ici dans le chat, puis demande-moi ce que tu veux.",
    hideLabel: 'Masquer le guide',
    guideButton: 'Guide',
    guideTooltip: "Besoin d'aide ? Afficher le guide",
    capabilities: [
      {
        icon: '🧭',
        title: 'Aperçu',
        description: 'Ce que cet assistant sait faire',
        prompt: 'Que peux-tu faire ? Donne-moi un aperçu.',
      },
      {
        icon: '📧',
        title: 'E-mail',
        description: 'Rechercher, résumer, préparer des brouillons',
        prompt: 'Comment peux-tu m’aider avec mes e-mails ?',
      },
      {
        icon: '📅',
        title: 'Calendrier',
        description: 'Consulter et créer des événements',
        prompt: 'Comment fonctionne le calendrier ?',
      },
      {
        icon: '📁',
        title: 'Fichiers et exports',
        description: 'Lit des fichiers, exporte en Excel, PDF, Word',
        prompt: 'Quels fichiers peux-tu lire et comment fonctionnent les exports ?',
      },
      {
        icon: '🤖',
        title: 'Spécialistes',
        description: 'Ventes, comptabilité, entrepôt et plus',
        prompt: 'Quels spécialistes peux-tu utiliser et quand ?',
      },
      {
        icon: '⏰',
        title: 'Tâches récurrentes',
        description: 'Tâches automatiques avec envoi par e-mail',
        prompt: 'Comment fonctionnent les tâches récurrentes ?',
      },
      {
        icon: '🧠',
        title: 'Mémoire',
        description: 'Retient les préférences que tu sauvegardes',
        prompt: 'Que peux-tu retenir sur moi et comment fonctionne la mémoire ?',
      },
      {
        icon: '🌐',
        title: 'Recherche web',
        description: 'Actualités et données à jour',
        prompt: 'Comment fonctionne la recherche web ?',
      },
    ],
  },
  es: {
    needHelp: '¿Necesitas ayuda?',
    subtitle:
      'Esto es lo que podemos hacer juntos. Haz clic en una tarjeta para descubrirlo — te respondo directamente aquí en el chat, luego pregúntame lo que quieras.',
    hideLabel: 'Ocultar guía',
    guideButton: 'Guía',
    guideTooltip: '¿Necesitas ayuda? Mostrar la guía',
    capabilities: [
      {
        icon: '🧭',
        title: 'Panorámica',
        description: 'Qué sabe hacer este asistente',
        prompt: '¿Qué puedes hacer? Dame una panorámica.',
      },
      {
        icon: '📧',
        title: 'Correo electrónico',
        description: 'Busca, resume, prepara borradores',
        prompt: '¿Cómo puedes ayudarme con el correo electrónico?',
      },
      {
        icon: '📅',
        title: 'Calendario',
        description: 'Consulta y crea eventos',
        prompt: '¿Cómo funciona el calendario?',
      },
      {
        icon: '📁',
        title: 'Archivos y exportaciones',
        description: 'Lee archivos, exporta a Excel, PDF, Word',
        prompt: '¿Qué archivos puedes leer y cómo funcionan las exportaciones?',
      },
      {
        icon: '🤖',
        title: 'Especialistas',
        description: 'Ventas, contabilidad, almacén y más',
        prompt: '¿Qué especialistas puedes usar y cuándo?',
      },
      {
        icon: '⏰',
        title: 'Tareas recurrentes',
        description: 'Trabajos automáticos con envío por correo',
        prompt: '¿Cómo funcionan las tareas recurrentes?',
      },
      {
        icon: '🧠',
        title: 'Memoria',
        description: 'Recuerda las preferencias que guardas',
        prompt: '¿Qué puedes recordar de mí y cómo funciona la memoria?',
      },
      {
        icon: '🌐',
        title: 'Búsqueda web',
        description: 'Noticias y datos actualizados',
        prompt: '¿Cómo funciona la búsqueda web?',
      },
    ],
  },
};

export const getWelcomeStrings = () => STRINGS[getWelcomeLang()];

// Controlled component: the parent decides when to mount it (auto on empty
// chat until dismissed, or forced via the Guide button). The X button
// records a permanent dismissal (handled by the parent via onDismiss).
export const WelcomeGuide = ({ gptName, gptLogo, onSelectPrompt, onDismiss }) => {
  const [lang, setLang] = useState(getWelcomeLang());
  const t = STRINGS[lang];

  const handleLangChange = (newLang) => {
    setWelcomeLang(newLang);
    setLang(newLang);
  };

  const handleClose = () => {
    if (onDismiss) onDismiss();
  };

  const handleCardClick = (prompt) => {
    if (onSelectPrompt && prompt) onSelectPrompt(prompt);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        maxWidth: 780,
        width: '100%',
        mx: 'auto',
        px: { xs: 1.5, sm: 3 },
        py: { xs: 2, sm: 6 },
        userSelect: 'none',
      }}
    >
      <IconButton
        size="small"
        onClick={handleClose}
        aria-label={t.hideLabel}
        sx={{ position: 'absolute', top: { xs: 8, sm: 12 }, right: { xs: 4, sm: 12 } }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
        {gptLogo ? (
          <Box
            component="img"
            src={gptLogo}
            alt=""
            sx={{ width: 34, height: 34, borderRadius: 2, objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            {(gptName || 'A').charAt(0).toUpperCase()}
          </Box>
        )}
        <Typography variant="overline" sx={{ lineHeight: 1.5, letterSpacing: 1, color: 'text.secondary' }}>
          {gptName || 'Assistant'}
        </Typography>
      </Box>

      <Typography
        variant="h5"
        fontWeight={700}
        textAlign={{ xs: 'center', sm: 'left' }}
        sx={{ fontSize: { xs: 20, sm: 24 } }}
        gutterBottom
      >
        {t.needHelp}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign={{ xs: 'center', sm: 'left' }}
        sx={{ mb: 2, maxWidth: 520, mx: { xs: 'auto', sm: 0 } }}
      >
        {t.subtitle}
      </Typography>

      {/* Language chips — switches the UI; the conversation language is
          remembered by the assistant itself (memory + System Guide skill) */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          alignItems: 'center',
          mb: { xs: 2, sm: 3 },
          justifyContent: { xs: 'center', sm: 'flex-start' },
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Lingua / Language:
        </Typography>
        {WELCOME_LANGS.map((l) => (
          <Chip
            key={l}
            label={l.toUpperCase()}
            size="small"
            onClick={() => handleLangChange(l)}
            color={l === lang ? 'primary' : 'default'}
            variant={l === lang ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* Capability cards */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 1.5 },
        }}
      >
        {t.capabilities.map((cap) => (
          <Box
            key={cap.title}
            component="button"
            onClick={() => handleCardClick(cap.prompt)}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: { xs: 1, sm: 0.5 },
              flex: { xs: '1 1 calc(50% - 12px)', sm: '1 1 calc(25% - 12px)' },
              minWidth: 0,
              p: { xs: 1.25, sm: 1.75 },
              textAlign: 'left',
              // Explicit color: buttons get the UA 'buttontext' color (near-black),
              // invisible on dark-mode cards — force the theme text color instead.
              color: 'text.primary',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2.5,
              cursor: 'pointer',
              transition: 'border-color .15s ease, transform .15s ease, box-shadow .15s ease',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-2px)',
                boxShadow: 1,
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Typography sx={{ fontSize: { xs: 18, sm: 20 }, lineHeight: 1 }}>{cap.icon}</Typography>
            <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>
              {cap.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.35, display: { xs: 'none', sm: 'block' } }}
            >
              {cap.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default WelcomeGuide;
